import { useState, useEffect, useRef } from "react";
import { Plus, Upload, Search, FileText, Check, X, AlertCircle, Clock, CheckCircle, CreditCard, Euro, Scan, Eye, Link, Package, AlertTriangle, Trash2, PlusCircle } from "lucide-react";
import { invoicesAPI, suppliersAPI } from "../services/api";
import api from "../services/api";

const STATUS_MAP = {
  pending: { label: "Pendente", cls: "bg-yellow-900/40 text-yellow-400 border-yellow-800" },
  paid: { label: "Pago", cls: "bg-green-900/40 text-green-400 border-green-800" },
  partial: { label: "Parcial", cls: "bg-blue-900/40 text-blue-400 border-blue-800" },
  overdue: { label: "Vencido", cls: "bg-red-900/40 text-red-400 border-red-800" },
};
const METHOD_MAP = { transfer: "Transferência", check: "Cheque", cash: "Numerário", direct_debit: "Débito Direto", card: "Cartão", other: "Outro" };

function StatusBadge({ status }) {
  const { label, cls } = STATUS_MAP[status] || STATUS_MAP.pending;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${cls}`}>{label}</span>;
}

const normalizeSearchText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const normalizeDigits = (value) =>
  String(value || '').replace(/\D+/g, '');

const matchesSearchTokensOrDigits = (...values) => {
  const queryRaw = values[values.length - 1];
  const queryText = normalizeSearchText(queryRaw);
  const queryDigits = normalizeDigits(queryRaw);

  if (!queryText && !queryDigits) return true;

  const rawValues = values.slice(0, -1).flatMap((v) => Array.isArray(v) ? v : [v]);

  const textHaystack = rawValues
    .map((v) => normalizeSearchText(v))
    .filter(Boolean)
    .join(' ');

  const digitsHaystack = rawValues
    .map((v) => normalizeDigits(v))
    .filter(Boolean)
    .join(' ');

  const textTokens = queryText.split(/\s+/).filter(Boolean);
  const textOk = !queryText || textTokens.every((token) => textHaystack.includes(token));
  const digitsOk = !queryDigits || digitsHaystack.includes(queryDigits);

  if (queryText && queryDigits) return textOk && digitsOk;
  if (queryText) return textOk;
  if (queryDigits) return digitsOk;
  return true;
};

const EMPTY = { supplier_id: "", supplier_name: "", invoice_number: "", invoice_date: new Date().toISOString().split("T")[0], due_date: "", items: [], subtotal: 0, vat_total: 0, vat_deductible: 0, total: 0, payment_method: "transfer", notes: "", stock_applied: true };

export default function PurchaseInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showOCR, setShowOCR] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showMapping, setShowMapping] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [ocrData, setOcrData] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrEntryMode, setOcrEntryMode] = useState(null);
  const [mappingItems, setMappingItems] = useState([]);
  const [mappingSearch, setMappingSearch] = useState({});
  const [allProducts, setAllProducts] = useState([]);
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [createProductIndex, setCreateProductIndex] = useState(null);
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [newProductForm, setNewProductForm] = useState({
    name: "",
    reference: "",
    barcode: "",
    category: "Geral",
    description: "",
    brand: "",
    cost_price: "",
    sell_price: "",
    vat_rate: 23,
    min_stock: 0,
    unit: "un",
    control_lots: true,
    sync_vendus: false,
  });
  const [payment, setPayment] = useState({ amount: 0, method: "transfer", date: new Date().toISOString().split("T")[0], reference: "", notes: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showMissingExpiry, setShowMissingExpiry] = useState(false);
  const [forceSaveAfterExpiry, setForceSaveAfterExpiry] = useState(false);
  const [missingExpiryDrafts, setMissingExpiryDrafts] = useState({});
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const fileRef = useRef();
  const linkedInvoiceOpenedRef = useRef(false);

  const hasUnsavedInvoiceWork = () => {
    if (ocrData) return true;
    if ((mappingItems || []).length > 0) return true;
    if ((form.items || []).length > 0) return true;

    return Boolean(
      form.supplier_id ||
      form.supplier_name ||
      form.invoice_number ||
      form.due_date ||
      form.notes ||
      Number(form.subtotal || 0) ||
      Number(form.vat_total || 0) ||
      Number(form.vat_deductible || 0) ||
      Number(form.total || 0)
    );
  };

  const confirmDiscardInvoiceWork = () => {
    if (!hasUnsavedInvoiceWork()) return true;
    return window.confirm("Existem dados preenchidos nesta fatura. Tem a certeza que pretende fechar e perder o trabalho já feito?");
  };

  const closeOCRModalSafely = () => {
    if (!confirmDiscardInvoiceWork()) return;
    setMissingExpiryDrafts({});
    setShowOCR(false);
  };

  const closeMappingModalSafely = () => {
    if (!confirmDiscardInvoiceWork()) return;
    setMissingExpiryDrafts({});
    setShowMapping(false);
  };

  const closeFormModalSafely = () => {
    if (!confirmDiscardInvoiceWork()) return;
    setMissingExpiryDrafts({});
    setShowForm(false);
  };

  const closeMissingExpiryModalSafely = () => {
    if (!confirmDiscardInvoiceWork()) return;
    setShowMissingExpiry(false);
    setForceSaveAfterExpiry(false);
    setMissingExpiryDrafts({});
  };

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [invRes, dashRes, supRes, prodRes] = await Promise.all([
        invoicesAPI.getAll(), invoicesAPI.getDashboard(),
        suppliersAPI.getAllFull(), api.get("/products?limit=2000")
      ]);
      setInvoices(invRes.data || []);
      setDashboard(dashRes.data);
      setSuppliers(supRes.data || []);
      setAllProducts(prodRes.data || []);
    } catch { } finally { setLoading(false); }
  };

  const handleOCR = async (file) => {
    setOcrLoading(true);
    setOcrData(null);
    try {
      const res = await invoicesAPI.ocr(file);
      const d = res.data;
      setOcrData(d);
      // Verifica se há itens que precisam de mapeamento manual
      const needsMapping = (d.items || []).filter(i => !i.product_id && i.product_suggestions?.length > 0);
      const noMatch = (d.items || []).filter(i => !i.product_id && (!i.product_suggestions || i.product_suggestions.length === 0));
      setMappingItems((d.items || []).map(i => ({ ...i, confirmed: !!i.product_id || !!i.mapped_from_memory })));
      setMappingSearch(Object.fromEntries(
        (d.items || []).map((i, idx) => [
          idx,
          i.product_match?.name || i.product_name || i.product_ref || ""
        ])
      ));
      setForm({
        supplier_id: d.supplier_id || "",
        supplier_name: d.supplier_name || "",
        invoice_number: d.invoice_number || "",
        invoice_date: d.invoice_date || new Date().toISOString().split("T")[0],
        due_date: d.due_date || "",
        items: d.items || [],
        subtotal: d.subtotal || 0,
        vat_total: d.vat_total || 0,
        vat_deductible: d.vat_deductible || d.vat_total || 0,
        total: d.total || 0,
        payment_method: d.payment_method || "transfer",
        notes: d.notes || "",
        ocr_confidence: d.confidence || null,
        stock_applied: ocrEntryMode === 'stock'
      });
      setShowOCR(false);
      if (ocrEntryMode === 'stock' && needsMapping.length > 0) {
        setShowMapping(true);
      } else {
        setShowForm(true);
      }
    } catch (e) {
      alert("Erro no OCR: " + (e.response?.data?.detail || e.message));
    } finally { setOcrLoading(false); }
  };

  const existingCategories = Array.from(
    new Set(
      (allProducts || [])
        .map((p) => String(p.category || "").trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b, "pt", { sensitivity: "base" }));

  const createEmptyManualItem = () => ({
    product_id: "",
    product_name: "",
    product_search: "",
    product_ref: "",
    quantity: 1,
    unit_price: 0,
    real_unit_cost: 0,
    vat_rate: 23,
    total: 0,
    lot_number: "",
    expiry_date: "",
    bonus_qty: 0,
    bonus_consolidated: false,
  });

  const recalcInvoiceTotals = (items) => {
    let subtotal = 0;
    let vatTotal = 0;
    let total = 0;

    const normalizedItems = (items || []).map((item) => {
      const qty = Number(item.quantity || 0);
      const unitCost = Number(item.real_unit_cost ?? item.unit_price ?? 0);
      const vatRate = Number(item.vat_rate || 23);

      const lineSubtotal = qty * unitCost;
      const lineVat = lineSubtotal * (vatRate / 100);
      const lineTotal = lineSubtotal + lineVat;

      subtotal += lineSubtotal;
      vatTotal += lineVat;
      total += lineTotal;

      return {
        ...item,
        quantity: qty,
        real_unit_cost: unitCost,
        unit_price: unitCost,
        vat_rate: vatRate,
        total: Number(lineSubtotal.toFixed(2)),
      };
    });

    return {
      items: normalizedItems,
      subtotal: Number(subtotal.toFixed(2)),
      vat_total: Number(vatTotal.toFixed(2)),
      vat_deductible: Number(vatTotal.toFixed(2)),
      total: Number(total.toFixed(2)),
    };
  };

  const getManualProductMatches = (item) => {
    const query = item?.product_search || item?.product_name || "";
    const queryText = normalizeSearchText(query);
    const queryDigits = normalizeDigits(query);

    if (!String(query || "").trim()) return [];

    const scored = (allProducts || [])
      .map((p) => {
        const name = String(p.name || "");
        const reference = String(p.reference || "");
        const barcode = String(p.barcode || "");
        const category = String(p.category || "");

        const normName = normalizeSearchText(name);
        const normReference = normalizeSearchText(reference);
        const normBarcodeDigits = normalizeDigits(barcode);
        const normCategory = normalizeSearchText(category);

        if (
          !matchesSearchTokensOrDigits(
            name,
            reference,
            barcode,
            category,
            query
          )
        ) {
          return null;
        }

        let score = 0;

        if (queryText) {
          const tokens = queryText.split(/\s+/).filter(Boolean);
          const nameWords = normName.split(/\s+/).filter(Boolean);

          if (normName === queryText) score += 1000;
          if (normReference === queryText) score += 900;

          if (normName.startsWith(queryText)) score += 700;
          if (normReference.startsWith(queryText)) score += 520;

          if (nameWords.some((w) => w.startsWith(queryText))) score += 480;

          if (normName.includes(queryText)) score += 320;
          if (normCategory.includes(queryText)) score += 90;

          const allTokensInName = tokens.length > 0 && tokens.every((t) => normName.includes(t));
          if (allTokensInName) score += 260;

          const sequentialWordsMatch = tokens.length > 1 && tokens.every((t) => nameWords.some((w) => w.startsWith(t)));
          if (sequentialWordsMatch) score += 220;

          const compactName = normName.replace(/\s+/g, "");
          const compactQuery = queryText.replace(/\s+/g, "");
          if (compactQuery && compactName.startsWith(compactQuery)) score += 180;

          const startsPenalty = Math.min(normName.indexOf(queryText), 50);
          if (normName.includes(queryText)) score += Math.max(0, 60 - startsPenalty);
        }

        if (queryDigits) {
          if (normBarcodeDigits === queryDigits) score += 1000;
          else if (normBarcodeDigits.startsWith(queryDigits)) score += 700;
          else if (queryDigits && normBarcodeDigits.includes(queryDigits)) score += 420;
        }

        score += Math.max(0, 40 - Math.min(normName.length, 40));

        return {
          ...p,
          __score: score,
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (b.__score !== a.__score) return b.__score - a.__score;
        return String(a.name || "").localeCompare(String(b.name || ""), "pt", {
          sensitivity: "base",
          numeric: true,
        });
      });

    return scored.slice(0, 8);
  };

  const selectManualProduct = (index, product) => {
    setForm((prev) => {
      const nextItems = [...(prev.items || [])];
      const current = nextItems[index] || createEmptyManualItem();

      nextItems[index] = {
        ...current,
        product_id: product.id,
        product_name: product.name || "",
        product_search: product.name || "",
        product_ref: product.reference || "",
        vat_rate: Number(product.vat_rate || current.vat_rate || 23),
        real_unit_cost: Number(current.real_unit_cost || product.cost_price || 0),
        unit_price: Number(current.real_unit_cost || product.cost_price || 0),
      };

      return {
        ...prev,
        ...recalcInvoiceTotals(nextItems),
      };
    });
  };

  const addManualItem = () => {
    setForm((prev) => ({
      ...prev,
      items: [...(prev.items || []), createEmptyManualItem()],
    }));
  };

  const updateManualItem = (index, field, value) => {
    setForm((prev) => {
      const nextItems = [...(prev.items || [])];
      const current = nextItems[index] || createEmptyManualItem();

      let parsedValue = value;

      if (["quantity", "real_unit_cost", "vat_rate"].includes(field)) {
        parsedValue = value === "" ? "" : Number(value);
      }

      nextItems[index] = {
        ...current,
        [field]: parsedValue,
      };

      if (field === "product_search") {
        nextItems[index].product_id = "";
        nextItems[index].product_name = value;
      }

      return {
        ...prev,
        ...recalcInvoiceTotals(nextItems),
      };
    });
  };

  const removeManualItem = (index) => {
    setForm((prev) => {
      const nextItems = (prev.items || []).filter((_, i) => i !== index);
      return {
        ...prev,
        ...recalcInvoiceTotals(nextItems),
      };
    });
  };

  const getFilteredProducts = (item, idx) => {
    const term = normalizeSearchText(mappingSearch[idx] || "");

    const suggestionIds = new Set((item.product_suggestions || []).map(s => s.id));
    const suggestions = (item.product_suggestions || []).slice(0, 8);

    const pool = (allProducts || []).filter((p) => !suggestionIds.has(p.id));

    if (!term) {
      return pool.slice(0, 60);
    }

    const words = term.split(/\s+/).filter(Boolean);

    return pool
      .map((p) => {
        const name = normalizeSearchText(p.name || "");
        const ref = normalizeSearchText(p.reference || "");
        const barcode = normalizeSearchText(p.barcode || "");

        let score = 0;
        if (name.includes(term)) score += 5;
        if (ref.includes(term)) score += 3;
        if (barcode.includes(term)) score += 2;

        for (const w of words) {
          if (name.includes(w)) score += 1;
          if (ref.includes(w)) score += 0.5;
        }

        return { ...p, _score: score };
      })
      .filter((p) => p._score > 0)
      .sort((a, b) => b._score - a._score || String(a.name || "").localeCompare(String(b.name || "")))
      .slice(0, 60);
  };


  const openCreateProductForItem = (idx) => {
    const item = mappingItems[idx];
    if (!item) return;

    setCreateProductIndex(idx);
    setNewProductForm({
      name: item.product_name || "",
      reference: item.product_ref || "",
      barcode: item.barcode || "",
      category: "Geral",
      description: item.product_name || "",
      brand: "",
      cost_price: item.real_unit_cost || item.unit_price || "",
      sell_price: "",
      vat_rate: Number(item.vat_rate || 23),
      min_stock: 0,
      unit: "un",
      control_lots: !!(item.lot_number || item.expiry_date),
      sync_vendus: false,
    });
    setShowCreateProductModal(true);
  };

  const handleCreateProductFromOCR = async () => {
    if (createProductIndex === null || createProductIndex === undefined) return;

    const item = mappingItems[createProductIndex];
    if (!item) return;

    if (!String(newProductForm.name || "").trim()) {
      alert("Preenche o nome do produto.");
      return;
    }

    setCreatingProduct(true);
    try {
      const payload = {
        item,
        supplier_id: form.supplier_id || ocrData?.supplier_id || "",
        name: newProductForm.name,
        reference: newProductForm.reference,
        barcode: newProductForm.barcode,
        category: newProductForm.category || "Geral",
        description: newProductForm.description,
        brand: newProductForm.brand,
        cost_price: Number(newProductForm.cost_price || item.real_unit_cost || item.unit_price || 0),
        sell_price: Number(newProductForm.sell_price || 0),
        vat_rate: Number(newProductForm.vat_rate || item.vat_rate || 23),
        min_stock: Number(newProductForm.min_stock || 0),
        unit: newProductForm.unit || "un",
        control_lots: !!newProductForm.control_lots,
        sync_vendus: !!newProductForm.sync_vendus,
      };

      const res = await invoicesAPI.createProductFromOCR(payload);
      const created = res.data?.product;
      const vendusWarning = res.data?.vendus_warning;

      if (!created?.id) {
        throw new Error("Produto criado sem id válido.");
      }

      setAllProducts((prev) => {
        const arr = Array.isArray(prev) ? prev.slice() : [];
        if (!arr.some((p) => p.id === created.id)) arr.unshift(created);
        return arr;
      });

      setMappingItems((prev) =>
        prev.map((it, i) =>
          i === createProductIndex
            ? {
                ...it,
                product_id: created.id,
                product_match: {
                  id: created.id,
                  name: created.name,
                  barcode: created.barcode || "",
                },
                confirmed: true,
                mapped_from_memory: false,
              }
            : it
        )
      );

      setMappingSearch((prev) => ({
        ...prev,
        [createProductIndex]: created.name || "",
      }));

      if (vendusWarning) {
        alert("Produto criado com sucesso.\n\n" + vendusWarning);
      }

      setShowCreateProductModal(false);
      setCreateProductIndex(null);
    } catch (e) {
      alert("Erro ao criar novo produto: " + (e.response?.data?.detail || e.message));
    } finally {
      setCreatingProduct(false);
    }
  };

  const confirmMapping = async () => {
    // Guarda mapeamentos na memória
    for (const item of mappingItems) {
      if (item.product_id && !item.mapped_from_memory) {
        const ref = item.product_ref || item.product_name;
        await api.post("/invoices/map-product", {
          invoice_ref: ref,
          product_id: item.product_id,
          supplier_id: ocrData?.supplier_id || "",
          invoice_name: item.product_name || "",
          update_product_name: !!item.update_product_name
        }).catch(() => {});
      }
    }
    // Atualiza form com mapeamentos confirmados
    setForm(f => ({ ...f, items: mappingItems }));
    setShowMapping(false);
    setShowForm(true);
  };


  const normalizeInvoiceItemsBeforeSave = (items) => {
    return (items || []).map((item) => ({
      ...item,
      quantity: Number(item.quantity || 0),
      unit_price: Number(item.unit_price || 0),
      vat_rate: Number(item.vat_rate || 23),
      total: Number(item.total || (Number(item.quantity || 0) * Number(item.unit_price || 0)) || 0),
      lot_number: (item.lot_number || '').trim() || null,
      expiry_date: (item.expiry_date || '').trim() || null,
      product_ref: item.product_ref || null,
      bonus_qty: Number(item.bonus_qty || 0),
      bonus_consolidated: !!item.bonus_consolidated,
      real_unit_cost: item.real_unit_cost !== undefined && item.real_unit_cost !== null && item.real_unit_cost !== ''
        ? Number(item.real_unit_cost)
        : null,
    }));
  };

  const openMissingExpiryModalIfNeeded = () => {
    if (!form.stock_applied) return false;
    const missing = (form.items || [])
      .map((item, realIndex) => ({ ...item, __realIndex: realIndex }))
      .filter((item) =>
        item.product_id &&
        String(item.lot_number || '').trim() &&
        !String(item.expiry_date || '').trim()
      );

    if (missing.length > 0) {
      const drafts = {};
      missing.forEach((item) => {
        drafts[item.__realIndex] = item.expiry_date || "";
      });
      setMissingExpiryDrafts(drafts);
      setShowMissingExpiry(true);
      return true;
    }
    return false;
  };

  const updateMissingExpiryField = (index, value) => {
    const today = new Date().toISOString().split("T")[0];

    if (value && value < today) {
      alert("A validade do lote não pode ser anterior à data de hoje.");
      return;
    }

    setMissingExpiryDrafts((prev) => ({
      ...prev,
      [index]: value,
    }));
  };

  const applyMissingExpiryDraftsToForm = () => {
    setForm((prev) => ({
      ...prev,
      items: (prev.items || []).map((item, i) => ({
        ...item,
        expiry_date: Object.prototype.hasOwnProperty.call(missingExpiryDrafts, i)
          ? (missingExpiryDrafts[i] || "").trim() || null
          : item.expiry_date
      })),
    }));
  };

  const openEditInvoice = (invoice) => {
    const preparedItems = (invoice?.items || []).map((item) => {
      const cost = Number(item.real_unit_cost ?? item.unit_price ?? 0);
      const qty = Number(item.quantity || 1);
      return {
        ...createEmptyManualItem(),
        ...item,
        product_search: item.product_name || "",
        product_name: item.product_name || "",
        quantity: qty,
        real_unit_cost: cost,
        unit_price: cost,
        vat_rate: Number(item.vat_rate || 23),
        total: Number(item.total || (qty * cost) || 0),
        lot_number: item.lot_number || "",
        expiry_date: item.expiry_date || "",
      };
    });

    setMissingExpiryDrafts({});
    setForceSaveAfterExpiry(false);
    setOcrData(null);
    setShowMapping(false);
    setEditingInvoiceId(invoice.id);
    setForm({
      ...EMPTY,
      ...invoice,
      items: preparedItems,
      subtotal: Number(invoice.subtotal || 0),
      vat_total: Number(invoice.vat_total || 0),
      vat_deductible: Number(invoice.vat_deductible || 0),
      total: Number(invoice.total || 0),
      payment_method: invoice.payment_method || "transfer",
      notes: invoice.notes || "",
    });
    setShowDetail(false);
    setSelected(null);
    setShowForm(true);
  };

  const openInvoiceDetail = async (invoice) => {
    if (!invoice?.id) return;

    try {
      const res = await invoicesAPI.getById(invoice.id);
      setSelected(res.data);
      setShowDetail(true);
    } catch (e) {
      alert("Erro ao abrir detalhe da fatura: " + (e.response?.data?.detail || e.message));
    }
  };

  useEffect(() => {
    if (linkedInvoiceOpenedRef.current || !invoices.length) return;
    const invoiceId = new URLSearchParams(window.location.search).get('invoice_id');
    if (!invoiceId) return;
    linkedInvoiceOpenedRef.current = true;
    const invoice = invoices.find((row) => String(row.id) === String(invoiceId));
    if (invoice) openInvoiceDetail(invoice);
  }, [invoices]);

  const saveInvoice = async () => {
    if (!form.supplier_name || !form.invoice_number) return;

    if (!forceSaveAfterExpiry) {
      const opened = openMissingExpiryModalIfNeeded();
      if (opened) return;
    }

    if (!window.confirm(editingInvoiceId ? "Quer guardar as alterações da fatura?" : "Quer guardar a fatura?")) {
      return;
    }

    setSaving(true);
    try {
      const normalizedItems = normalizeInvoiceItemsBeforeSave(form.items || []);
      const payload = {
        ...form,
        items: normalizedItems,
      };

      if (editingInvoiceId) {
        await api.put(`/invoices/${editingInvoiceId}`, payload);
      } else {
        await invoicesAPI.create(payload);
      }

      await loadAll();
      setForm(EMPTY);
      setOcrData(null);
      setMappingItems([]);
      setMappingSearch({});
      setMissingExpiryDrafts({});
      setForceSaveAfterExpiry(false);
      setEditingInvoiceId(null);
      setShowForm(false);
      alert(editingInvoiceId ? "Fatura atualizada com sucesso." : "Fatura criada com sucesso.");
    } catch (e) {
      alert("Erro ao guardar fatura: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const deleteInvoice = async (inv) => {
    if (!inv?.id) return;

    const first = window.confirm(
      `Vai apagar a fatura ${inv.invoice_number || ""}.\n\nEsta ação pode reverter stock, lotes e saldo do fornecedor. Pretende continuar?`
    );
    if (!first) return;

    const second = window.confirm(
      `Confirma MESMO que pretende apagar definitivamente a fatura ${inv.invoice_number || ""}?`
    );
    if (!second) return;

    setSaving(true);
    try {
      await invoicesAPI.delete(inv.id);
      if (showDetail && selected?.id === inv.id) {
        setShowDetail(false);
        setSelected(null);
      }
      await loadAll();
      alert("Fatura apagada com sucesso.");
    } catch (e) {
      alert("Erro ao apagar fatura: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const openPaymentModal = (invoice) => {
    const outstanding = Math.max(0, Number(invoice?.total || 0) - Number(invoice?.amount_paid || 0));
    setSelected(invoice);
    setPayment({
      amount: Number(outstanding.toFixed(2)),
      method: invoice?.payment_method || "transfer",
      date: new Date().toISOString().split("T")[0],
      reference: "",
      notes: "",
    });
    setShowPayment(true);
  };

  const registerPayment = async () => {
    if (!selected?.id || saving) return;
    const outstanding = Math.max(0, Number(selected.total || 0) - Number(selected.amount_paid || 0));
    const amount = Number(payment.amount || 0);
    if (amount <= 0 || amount > outstanding + 0.005) {
      alert(`O pagamento deve estar entre 0,01€ e ${outstanding.toFixed(2)}€.`);
      return;
    }
    if (!payment.date) {
      alert("Indique a data efetiva do pagamento.");
      return;
    }

    setSaving(true);
    try {
      await invoicesAPI.registerPayment(selected.id, {
        amount,
        payment_method: payment.method,
        payment_date: payment.date,
        reference: payment.reference || undefined,
        notes: payment.notes || undefined,
      });
      setShowPayment(false);
      setSelected(null);
      await loadAll();
      alert("Pagamento registado com sucesso.");
    } catch (e) {
      alert("Erro ao registar pagamento: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };






  const filtered = invoices.filter((i) => {
    const queryRaw = String(search || '').trim();

    const matchesSearch =
      !queryRaw ||
      matchesSearchTokensOrDigits(
        [
          i?.supplier_name,
          i?.invoice_number,
          i?.supplier_nif,
          i?.reference,
          i?.payment_status,
          i?.payment_method,
          i?.notes,
          i?.id,
          i?.invoice_date,
          i?.due_date,
          ...(Array.isArray(i?.items)
            ? i.items.flatMap((item) => [
                item?.product_name,
                item?.product_search,
                item?.product_ref,
                item?.barcode,
                item?.lot_number,
                item?.expiry_date,
              ])
            : []
          ),
        ],
        queryRaw
      );

    const matchesStatus =
      !filterStatus ||
      filterStatus === 'all' ||
      String(i?.payment_status || '') === filterStatus;

    return matchesSearch && matchesStatus;
  });



  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Faturas de Compra</h1>
          <p className="text-gray-400 text-sm mt-0.5">Gestão de faturas, pagamentos e stock</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setOcrEntryMode(null); setShowOCR(true); }} className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 transition-colors">
            <Scan className="w-4 h-4" /> OCR Fatura
          </button>
          <button onClick={() => { setForm(EMPTY); setOcrData(null); setEditingInvoiceId(null); setShowForm(true); }} className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-xl text-sm flex items-center gap-2 transition-colors">
            <Plus className="w-4 h-4" /> Nova Fatura
          </button>
        </div>
      </div>

      {/* Dashboard */}
      {dashboard && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: "A Pagar", value: dashboard.total_pending, color: "text-yellow-400", icon: Clock },
            { label: "Vencido", value: dashboard.total_overdue, color: "text-red-400", icon: AlertCircle },
            { label: "Pago este Mês", value: dashboard.total_paid_month, color: "text-green-400", icon: CheckCircle },
            { label: "IVA Dedutível", value: dashboard.total_vat_deductible_month, color: "text-blue-400", icon: Euro },
          ].map(({ label, value, color, icon: Icon }) => (
            <div key={label} className="bg-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2"><Icon className={`w-4 h-4 ${color}`} /><p className="text-gray-400 text-xs">{label}</p></div>
              <p className={`text-xl font-bold ${color}`}>{(value || 0).toFixed(2)}€</p>
            </div>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar..." className="w-full bg-gray-800 border border-gray-700 text-white rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-green-600" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="bg-gray-800 border border-gray-700 text-white rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-green-600">
          <option value="">Todos os estados</option>
          {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {/* Tabela */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              {["Nº Fatura","Fornecedor","Data","Vencimento","Total","IVA Dedutível","Estado","Ações"].map(h => (
                <th key={h} className="text-left text-gray-400 text-xs font-medium px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center text-gray-500 py-8 text-sm">A carregar...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-gray-500 py-8 text-sm">Nenhuma fatura encontrada.</td></tr>
            ) : filtered.map(inv => (
              <tr key={inv.id} className="border-b border-gray-700/50 hover:bg-gray-700/30 transition-colors">
                <td className="px-4 py-3 text-white text-sm font-medium">{inv.invoice_number}</td>
                <td className="px-4 py-3 text-gray-300 text-sm">{inv.supplier_name}</td>
                <td className="px-4 py-3 text-gray-400 text-sm">{inv.invoice_date}</td>
                <td className="px-4 py-3 text-sm">
                  <span className={inv.due_date && inv.due_date < new Date().toISOString().split("T")[0] && inv.payment_status !== "paid" ? "text-red-400 font-medium" : "text-gray-400"}>
                    {inv.due_date || "—"}
                  </span>
                </td>
                <td className="px-4 py-3 text-white text-sm font-bold">{inv.total?.toFixed(2)}€</td>
                <td className="px-4 py-3 text-blue-400 text-sm">{(inv.vat_deductible || inv.vat_total || 0).toFixed(2)}€</td>
                <td className="px-4 py-3"><StatusBadge status={inv.payment_status} /></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => openInvoiceDetail(inv)} className="bg-gray-700 hover:bg-gray-600 text-white p-1.5 rounded-lg" title="Ver"><Eye className="w-3.5 h-3.5" /></button>
                    {inv.payment_status !== "paid" && (
                      <button onClick={() => openPaymentModal(inv)} className="bg-green-700 hover:bg-green-600 text-white p-1.5 rounded-lg" title="Pagar">
                        <CreditCard className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteInvoice(inv)}
                      className="bg-red-800 hover:bg-red-700 text-white p-1.5 rounded-lg"
                      title="Apagar fatura"
                      disabled={saving}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal OCR Upload */}
      {showOCR && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-white">OCR — Ler Fatura</h2>
              <button onClick={closeOCRModalSafely} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            {!ocrEntryMode ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-300">Como pretende registar esta fatura?</p>
                <button onClick={() => setOcrEntryMode('accounting')} className="w-full text-left rounded-xl border border-blue-700 bg-blue-950/30 p-4 hover:bg-blue-900/40">
                  <span className="block font-semibold text-blue-300">Apenas entrada contabilística</span>
                  <span className="block text-xs text-gray-400 mt-1">Regista fornecedor, número, datas, valores e IVA, sem alterar produtos, lotes ou stock.</span>
                </button>
                <button onClick={() => setOcrEntryMode('stock')} className="w-full text-left rounded-xl border border-emerald-700 bg-emerald-950/30 p-4 hover:bg-emerald-900/40">
                  <span className="block font-semibold text-emerald-300">Contabilizar e dar entrada em stock</span>
                  <span className="block text-xs text-gray-400 mt-1">Além da fatura, associa os artigos e cria os movimentos de stock e lotes.</span>
                </button>
              </div>
            ) : <div onClick={() => fileRef.current.click()} className="border-2 border-dashed border-gray-600 hover:border-blue-500 rounded-xl p-10 text-center cursor-pointer transition-colors">
              {ocrLoading ? (
                <div className="space-y-3">
                  <Scan className="w-10 h-10 text-blue-400 mx-auto animate-pulse" />
                  <p className="text-white font-medium">A analisar com IA...</p>
                  <p className="text-gray-500 text-sm">Aguarda alguns segundos</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <Upload className="w-10 h-10 text-gray-500 mx-auto" />
                  <p className="text-white font-medium">Clica para carregar fatura</p>
                  <p className="text-gray-500 text-sm">JPG, PNG ou PDF</p>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={e => e.target.files[0] && handleOCR(e.target.files[0])} />
            </div>}
            <div className="mt-4 bg-blue-900/20 border border-blue-800 rounded-xl p-3 text-blue-300 text-xs">
              A IA extrai fornecedor, artigos, IVA, datas e totais. O fornecedor é atualizado ou criado se necessário. O stock só muda quando escolher essa opção.
            </div>
          </div>
        </div>
      )}

      {/* Modal Mapeamento de Produtos */}
      {showMapping && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <div>
                <h2 className="text-lg font-bold text-white">Associar Produtos</h2>
                <p className="text-gray-400 text-sm mt-0.5">Confirma a associação dos artigos da fatura com os produtos em sistema</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowMapping(false); setShowOCR(true); }}
                  className="px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm"
                >
                  Voltar
                </button>
                <button onClick={closeMappingModalSafely} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
            </div>
            {ocrData?.supplier_created && (
              <div className="mx-6 mt-4 bg-green-900/20 border border-green-800 rounded-xl p-3 text-green-300 text-sm flex items-center gap-2">
                <Check className="w-4 h-4 shrink-0" /> Fornecedor <strong>{ocrData.supplier_name}</strong> criado automaticamente.
              </div>
            )}
            <div className="p-6 space-y-3">
              {mappingItems.map((item, idx) => (
                <div key={idx} className={`bg-gray-800 rounded-xl p-4 border ${item.confirmed ? 'border-green-800' : 'border-yellow-800'}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-white font-medium text-sm">{item.product_name}</p>
                      <div className="flex gap-3 mt-1 text-xs text-gray-500">
                        <span>Qtd: {item.quantity}</span>
                        <span>Preço: {item.unit_price?.toFixed(2)}€</span>
                        {item.lot_number && <span className="text-blue-400">Lote: {item.lot_number}</span>}
                        {item.expiry_date && <span className="text-orange-400">Val: {item.expiry_date}</span>}
                      </div>
                    </div>
                    {item.confirmed ? (
                      <span className="flex items-center gap-1 text-green-400 text-xs"><Check className="w-3.5 h-3.5" /> Associado</span>
                    ) : (
                      <span className="flex items-center gap-1 text-yellow-400 text-xs"><AlertTriangle className="w-3.5 h-3.5" /> Por confirmar</span>
                    )}
                  </div>
                  {item.mapped_from_memory && item.product_match && (
                    <div className="bg-green-900/20 rounded-lg p-2 text-green-300 text-xs flex items-center gap-2">
                      <Link className="w-3.5 h-3.5" /> Mapeamento em memória: <strong>{item.product_match.name}</strong>
                    </div>
                  )}
                  {!item.mapped_from_memory && (
                    <div className="space-y-2">
                      <label className="block text-gray-400 text-xs mb-1">Associar a produto em sistema:</label>

                      <input
                        value={mappingSearch[idx] || ""}
                        onChange={e => {
                          const value = e.target.value;
                          setMappingSearch(prev => ({ ...prev, [idx]: value }));
                        }}
                        placeholder="Pesquisar artigo do sistema..."
                        className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                      />

                      <select
                        value={item.product_id || ""}
                        onChange={e => {
                          const pid = e.target.value;
                          const prod = allProducts.find(p => p.id === pid);
                          setMappingItems(prev => prev.map((it, i) => i === idx ? { ...it, product_id: pid, product_match: prod, confirmed: !!pid } : it));
                          if (prod) {
                            setMappingSearch(prev => ({ ...prev, [idx]: prod.name || "" }));
                          }
                        }}
                        className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                      >
                        <option value="">-- Não associar --</option>

                        {item.product_suggestions?.length > 0 && (
                          <optgroup label="Sugestões">
                            {item.product_suggestions.map(s => (
                              <option key={s.id} value={s.id}>
                                ⭐ {s.name}
                              </option>
                            ))}
                          </optgroup>
                        )}

                        <optgroup label="Resultados da pesquisa">
                          {getFilteredProducts(item, idx).map(p => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </optgroup>
                      </select>

                      <div className="flex gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => openCreateProductForItem(idx)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm"
                        >
                          <PlusCircle className="w-4 h-4" />
                          Criar novo produto
                        </button>
                      </div>

                      <p className="text-[11px] text-gray-500">
                        Escreve para filtrar artigos. As sugestões automáticas continuam no topo.
                      </p>
                      {item.product_id && item.product_match?.name && normalizeSearchText(item.product_match.name) !== normalizeSearchText(item.product_name) && (
                        <label className="flex items-start gap-2 rounded-lg border border-amber-800/60 bg-amber-950/30 p-2 text-xs text-amber-100">
                          <input type="checkbox" checked={!!item.update_product_name}
                            onChange={(e) => setMappingItems(prev => prev.map((it, i) => i === idx ? {...it, update_product_name: e.target.checked} : it))} />
                          Atualizar o nome da ficha para “{item.product_name}”. A referência do fornecedor fica memorizada mesmo sem alterar o nome.
                        </label>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-3 p-6 border-t border-gray-800">
              <button
                onClick={() => { setShowMapping(false); setShowOCR(true); }}
                className="bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-xl text-sm px-5"
              >
                Voltar
              </button>
              <button onClick={() => { setShowMapping(false); setShowForm(true); }} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-xl text-sm">Ignorar e Continuar</button>
              <button onClick={confirmMapping} className="flex-1 bg-green-600 hover:bg-green-500 text-white py-2.5 rounded-xl text-sm flex items-center justify-center gap-2">
                <Check className="w-4 h-4" /> Confirmar Associações
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Modal Criar Novo Produto a partir do OCR */}
      {showCreateProductModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <div>
                <h2 className="text-lg font-bold text-white">Criar Novo Produto</h2>
                <p className="text-gray-400 text-sm mt-0.5">Criado a partir da linha OCR da fatura</p>
              </div>
              <button
                onClick={() => { if (!creatingProduct) setShowCreateProductModal(false); }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-gray-400 text-xs mb-1">Nome *</label>
                <input
                  value={newProductForm.name}
                  onChange={(e) => setNewProductForm((p) => ({ ...p, name: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Referência</label>
                  <input
                    value={newProductForm.reference}
                    onChange={(e) => setNewProductForm((p) => ({ ...p, reference: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Código de barras</label>
                  <input
                    value={newProductForm.barcode}
                    onChange={(e) => setNewProductForm((p) => ({ ...p, barcode: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Categoria</label>
                  <select
                    value={newProductForm.category}
                    onChange={(e) => setNewProductForm((p) => ({ ...p, category: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                  >
                    <option value="">Selecionar categoria...</option>
                    {existingCategories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                    {!existingCategories.includes("Geral") && <option value="Geral">Geral</option>}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Marca</label>
                  <input
                    value={newProductForm.brand}
                    onChange={(e) => setNewProductForm((p) => ({ ...p, brand: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-xs mb-1">Descrição</label>
                <textarea
                  rows={2}
                  value={newProductForm.description}
                  onChange={(e) => setNewProductForm((p) => ({ ...p, description: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Custo</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={newProductForm.cost_price}
                    onChange={(e) => setNewProductForm((p) => ({ ...p, cost_price: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Venda</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newProductForm.sell_price}
                    onChange={(e) => setNewProductForm((p) => ({ ...p, sell_price: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">IVA %</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newProductForm.vat_rate}
                    onChange={(e) => setNewProductForm((p) => ({ ...p, vat_rate: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Stock mín.</label>
                  <input
                    type="number"
                    step="1"
                    value={newProductForm.min_stock}
                    onChange={(e) => setNewProductForm((p) => ({ ...p, min_stock: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Unidade</label>
                  <input
                    value={newProductForm.unit}
                    onChange={(e) => setNewProductForm((p) => ({ ...p, unit: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                  />
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={!!newProductForm.control_lots}
                      onChange={(e) => setNewProductForm((p) => ({ ...p, control_lots: e.target.checked }))}
                    />
                    Controlar lotes
                  </label>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl p-4">
                <label className="inline-flex items-center gap-2 text-sm text-gray-200">
                  <input
                    type="checkbox"
                    checked={!!newProductForm.sync_vendus}
                    onChange={(e) => setNewProductForm((p) => ({ ...p, sync_vendus: e.target.checked }))}
                  />
                  Sincronizar também com Vendus
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  A referência será criada com segurança para evitar duplicados. Se o Vendus falhar, o produto fica criado localmente na mesma.
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-800">
              <button
                onClick={() => { if (!creatingProduct) setShowCreateProductModal(false); }}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-xl text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateProductFromOCR}
                disabled={creatingProduct || !String(newProductForm.name || "").trim()}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm flex items-center justify-center gap-2"
              >
                <PlusCircle className="w-4 h-4" />
                {creatingProduct ? "A criar..." : "Criar produto"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Formulário */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <div>
                <h2 className="text-lg font-bold text-white">
                  {editingInvoiceId ? "Editar Fatura" : (ocrData ? "Confirmar Fatura (OCR)" : "Nova Fatura")}
                </h2>
                {ocrData?.ocr_confidence && <p className="text-gray-400 text-xs mt-0.5">Confiança OCR: {Math.round(ocrData.ocr_confidence * 100)}%{ocrData.supplier_created ? " · Fornecedor criado automaticamente" : ""}</p>}
              </div>
              <button onClick={closeFormModalSafely} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-gray-400 text-xs mb-1">Fornecedor</label>
                  <select value={form.supplier_id} onChange={e => { const s = suppliers.find(x => x.id === e.target.value); setForm(f => ({ ...f, supplier_id: e.target.value, supplier_name: s?.name || f.supplier_name })); }} className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600">
                    <option value="">-- Selecionar --</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                  {!form.supplier_id && <input value={form.supplier_name} onChange={e => setForm(f => ({ ...f, supplier_name: e.target.value }))} placeholder="Nome do fornecedor" className="w-full mt-2 bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />}
                </div>
                {[["Nº Fatura *","invoice_number","text"],["Data Fatura","invoice_date","date"],["Data Vencimento","due_date","date"]].map(([label,field,type]) => (
                  <div key={field}>
                    <label className="block text-gray-400 text-xs mb-1">{label}</label>
                    <input type={type} value={form[field]||""} onChange={e => setForm(f=>({...f,[field]:e.target.value}))} className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
                  </div>
                ))}
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Método Pagamento</label>
                  <select value={form.payment_method||"transfer"} onChange={e => setForm(f=>({...f,payment_method:e.target.value}))} className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600">
                    {Object.entries(METHOD_MAP).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div className="bg-gray-800 rounded-xl p-4 grid grid-cols-4 gap-4">
                {[["Subtotal €","subtotal"],["IVA €","vat_total"],["IVA Dedutível €","vat_deductible"],["Total €","total"]].map(([label,field])=>(
                  <div key={field}>
                    <label className="block text-gray-400 text-xs mb-1">{label}</label>
                    <input type="number" step="0.01" value={form[field]||0} onChange={e=>setForm(f=>({...f,[field]:parseFloat(String(e.target.value).replace(',', '.'))||0}))} className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
                  </div>
                ))}
              </div>
              <div className="bg-gray-800/70 rounded-2xl p-4 border border-gray-700">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
                  <div>
                    <label className="block text-gray-300 text-sm font-medium">Artigos da fatura</label>
                    <p className="text-xs text-gray-500 mt-1">
                      Adiciona manualmente os artigos, escolhe o produto do sistema e preenche quantidade, lote, validade e preço de custo.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setCreateProductIndex(null);
                        setShowCreateProductModal(true);
                      }}
                      className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-xl text-sm flex items-center gap-2"
                    >
                      <PlusCircle className="w-4 h-4" />
                      Criar novo artigo
                    </button>
                    <button
                      type="button"
                      onClick={addManualItem}
                      className="bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-2 rounded-xl text-sm flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Adicionar artigo
                    </button>
                  </div>
                </div>

                {(form.items || []).length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-600 p-6 text-center text-sm text-gray-400">
                    Ainda não há artigos nesta fatura. Clica em <span className="text-white font-medium">Adicionar artigo</span>.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {(form.items || []).map((item, i) => {
                      const matches = getManualProductMatches(item);
                      return (
                        <div key={i} className="rounded-xl border border-gray-700 bg-gray-900/60 p-4">
                          <div className="flex items-center justify-between gap-3 mb-3">
                            <div className="flex items-center gap-2">
                              <Package className="w-4 h-4 text-emerald-400" />
                              <p className="text-sm font-medium text-white">Artigo {i + 1}</p>
                              {item.product_id ? (
                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-900/40 text-emerald-400 border border-emerald-800">
                                  Associado ao stock
                                </span>
                              ) : (
                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-yellow-900/30 text-yellow-400 border border-yellow-800">
                                  Por associar
                                </span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeManualItem(i)}
                              className="text-red-400 hover:text-red-300"
                              title="Remover artigo"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-6 xl:grid-cols-12 gap-3">
                            <div className="md:col-span-6 xl:col-span-5">
                              <label className="block text-gray-400 text-xs mb-1">Pesquisar produto</label>
                              <input
                                value={item.product_search || item.product_name || ""}
                                onChange={(e) => updateManualItem(i, "product_search", e.target.value)}
                                placeholder="Nome, referência ou código"
                                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                              />
                              {!item.product_id && String(item.product_search || "").trim().length >= 2 && matches.length > 0 && (
                                <div className="mt-2 rounded-lg border border-gray-700 bg-gray-800 max-h-44 overflow-auto">
                                  {matches.map((prod) => (
                                    <button
                                      key={prod.id}
                                      type="button"
                                      onClick={() => selectManualProduct(i, prod)}
                                      className="w-full text-left px-3 py-2 border-b border-gray-700 last:border-b-0 hover:bg-gray-700"
                                    >
                                      <div className="text-sm text-white">{prod.name}</div>
                                      <div className="text-[11px] text-gray-400">
                                        Ref: {prod.reference || "—"} | Código: {prod.barcode || "—"}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="md:col-span-2 xl:col-span-1">
                              <label className="block text-gray-400 text-xs mb-1">Quantidade</label>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                inputMode="numeric"
                                value={item.quantity ?? 1}
                                onChange={(e) => updateManualItem(i, "quantity", e.target.value)}
                                className="w-full min-w-0 bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                            </div>

                            <div className="md:col-span-2 xl:col-span-2">
                              <label className="block text-gray-400 text-xs mb-1">Preço custo</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.real_unit_cost ?? 0}
                                onChange={(e) => updateManualItem(i, "real_unit_cost", e.target.value)}
                                className="w-full min-w-0 bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                            </div>

                            <div className="md:col-span-2 xl:col-span-1">
                              <label className="block text-gray-400 text-xs mb-1">IVA %</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={item.vat_rate ?? 23}
                                onChange={(e) => updateManualItem(i, "vat_rate", e.target.value)}
                                className="w-full min-w-0 bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                            </div>

                            <div className="md:col-span-3 xl:col-span-1">
                              <label className="block text-gray-400 text-xs mb-1">Lote</label>
                              <input
                                value={item.lot_number || ""}
                                onChange={(e) => updateManualItem(i, "lot_number", e.target.value)}
                                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                              />
                            </div>

                            <div className="md:col-span-3 xl:col-span-2">
                              <label className="block text-gray-400 text-xs mb-1">Validade</label>
                              <input
                                type="date"
                                value={item.expiry_date || ""}
                                onChange={(e) => updateManualItem(i, "expiry_date", e.target.value)}
                                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {form.items?.length > 0 && (
                  <div className="mt-4">
                    <label className="block text-gray-400 text-xs mb-2">
                      Resumo dos artigos ({form.items.length}) {form.items.filter(i=>i.product_id).length > 0 && <span className="text-green-400">· {form.items.filter(i=>i.product_id).length} associados ao stock</span>}
                    </label>
                    <div className="bg-gray-800 rounded-xl overflow-hidden">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-gray-700"><th className="text-left text-gray-400 text-xs px-3 py-2">Produto</th><th className="text-right text-gray-400 text-xs px-3 py-2">Qtd</th><th className="text-right text-gray-400 text-xs px-3 py-2">Preço</th><th className="text-right text-gray-400 text-xs px-3 py-2">IVA</th><th className="text-center text-gray-400 text-xs px-3 py-2">Lote/Val</th><th className="text-center text-gray-400 text-xs px-3 py-2">Stock</th></tr></thead>
                        <tbody>{form.items.map((item,i)=>(
                          <tr key={i} className="border-b border-gray-700/50">
                            <td className="px-3 py-2 text-gray-300">{item.product_name || item.product_search || "—"}</td>
                            <td className="px-3 py-2 text-gray-400 text-right">{item.quantity}</td>
                            <td className="px-3 py-2 text-gray-400 text-right">{Number(item.unit_price || 0).toFixed(2)}€</td>
                            <td className="px-3 py-2 text-gray-400 text-right">{item.vat_rate}%</td>
                            <td className="px-3 py-2 text-center">
                              {item.lot_number && <span className="text-blue-400 text-xs block">L: {item.lot_number}</span>}
                              {item.expiry_date && <span className="text-orange-400 text-xs block">V: {item.expiry_date}</span>}
                            </td>
                            <td className="px-3 py-2 text-center">{item.product_id ? <Check className="w-4 h-4 text-green-400 mx-auto"/> : <X className="w-4 h-4 text-gray-600 mx-auto"/>}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1">Notas</label>
                <textarea value={form.notes||""} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2} className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600 resize-none" />
              </div>
              {ocrData && (
                <div className={`rounded-xl border p-3 text-sm ${form.stock_applied ? 'border-emerald-800 bg-emerald-950/20 text-emerald-300' : 'border-blue-800 bg-blue-950/20 text-blue-300'}`}>
                  {form.stock_applied ? 'Esta fatura criará movimentos de stock e lotes.' : 'Esta fatura será apenas contabilística e não alterará o stock.'}
                </div>
              )}
            </div>
            <div className="flex gap-3 p-6 border-t border-gray-800">
              {ocrData && (
                <button
                  onClick={() => { setShowForm(false); setShowMapping(true); }}
                  className="bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-xl text-sm px-5"
                >
                  Voltar
                </button>
              )}
              <button onClick={closeFormModalSafely} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-xl text-sm">Cancelar</button>
              <button onClick={saveInvoice} disabled={saving||!form.invoice_number} className="flex-1 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm flex items-center justify-center gap-2">
                <Check className="w-4 h-4"/>{saving ? "A guardar..." : (editingInvoiceId ? "Guardar Alterações" : (form.stock_applied ? "Guardar e Atualizar Stock" : "Guardar Apenas na Contabilidade"))}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Modal Validades em Falta */}
      {showMissingExpiry && (() => {
        const missingExpiryItems = (form.items || [])
          .map((item, realIndex) => ({ ...item, __realIndex: realIndex }))
          .filter((item) =>
            item.product_id &&
            String(item.lot_number || '').trim() &&
            !String(item.expiry_date || '').trim()
          );

        return (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div
              className="bg-gray-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-800">
                <div>
                  <h2 className="text-lg font-bold text-white">Completar Validades dos Lotes</h2>
                  <p className="text-gray-400 text-sm mt-0.5">As linhas não desaparecem até confirmar no botão final.</p>
                </div>
                <button
                  onClick={closeMissingExpiryModalSafely}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {missingExpiryItems.length === 0 ? (
                  <div className="bg-gray-800 rounded-xl p-4 text-sm text-gray-300">
                    Não existem lotes pendentes de validade.
                  </div>
                ) : (
                  missingExpiryItems.map((item, idx) => (
                    <div key={`${item.__realIndex}-${idx}`} className="bg-gray-800 rounded-xl p-4 border border-yellow-800">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div className="md:col-span-2">
                          <p className="text-white font-medium text-sm">{item.product_name}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            Lote: <span className="text-blue-400">{item.lot_number}</span> · Qtd: {item.quantity}
                          </p>
                        </div>
                        <div>
                          <label className="block text-gray-400 text-xs mb-1">Validade</label>
                          <input
                            type="date"
                            min={new Date().toISOString().split("T")[0]}
                            value={missingExpiryDrafts[item.__realIndex] || ""}
                            onChange={(e) => updateMissingExpiryField(item.__realIndex, e.target.value)}
                            className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600"
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex gap-3 p-6 border-t border-gray-800">
                <button
                  onClick={closeMissingExpiryModalSafely}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-xl text-sm"
                >
                  Voltar
                </button>
                <button
                  onClick={() => {
                    const today = new Date().toISOString().split("T")[0];

                    const stillMissing = missingExpiryItems.some((item) =>
                      !String(missingExpiryDrafts[item.__realIndex] || '').trim()
                    );
                    if (stillMissing) {
                      alert("Ainda existem lotes sem validade.");
                      return;
                    }

                    const hasPastDate = missingExpiryItems.some((item) => {
                      const v = String(missingExpiryDrafts[item.__realIndex] || '').trim();
                      return v && v < today;
                    });
                    if (hasPastDate) {
                      alert("Existe pelo menos uma validade inferior à data de hoje. Corrige antes de continuar.");
                      return;
                    }

                    if (!window.confirm("Confirma as alterações das validades destes lotes?")) {
                      return;
                    }

                    applyMissingExpiryDraftsToForm();
                    setShowMissingExpiry(false);
                    setForceSaveAfterExpiry(false);
                    setMissingExpiryDrafts({});
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-500 text-white py-2.5 rounded-xl text-sm flex items-center justify-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Confirmar Validades
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal Detalhe */}
      {showDetail && selected && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-800">
              <div>
                <h2 className="text-lg font-bold text-white">Detalhe da Fatura</h2>
                <p className="text-gray-400 text-sm mt-0.5">{selected.invoice_number || "-"}</p>
              </div>
              <button
                onClick={() => { setShowDetail(false); setSelected(null); }}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Fornecedor</p>
                  <p className="text-white text-sm font-medium">{selected.supplier_name || "—"}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Data Fatura</p>
                  <p className="text-white text-sm font-medium">{selected.invoice_date || "—"}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Vencimento</p>
                  <p className="text-white text-sm font-medium">{selected.due_date || "—"}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4">
                  <p className="text-gray-400 text-xs mb-1">Estado</p>
                  <div className="mt-1"><StatusBadge status={selected.payment_status} /></div>
                </div>
              </div>

              <div className="bg-gray-800 rounded-xl p-4 grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <p className="text-gray-400 text-xs mb-1">Subtotal</p>
                  <p className="text-white font-semibold">{(selected.subtotal || 0).toFixed(2)}€</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">IVA</p>
                  <p className="text-white font-semibold">{(selected.vat_total || 0).toFixed(2)}€</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">IVA Dedutível</p>
                  <p className="text-blue-400 font-semibold">{(selected.vat_deductible || selected.vat_total || 0).toFixed(2)}€</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Pago</p>
                  <p className="text-green-400 font-semibold">{(selected.amount_paid || 0).toFixed(2)}€</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs mb-1">Total</p>
                  <p className="text-white font-bold">{(selected.total || 0).toFixed(2)}€</p>
                </div>
              </div>

              <div>
                <h3 className="text-white font-semibold mb-3">Artigos</h3>
                <div className="bg-gray-800 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th className="text-left text-gray-400 text-xs px-3 py-2">Produto</th>
                        <th className="text-right text-gray-400 text-xs px-3 py-2">Qtd</th>
                        <th className="text-right text-gray-400 text-xs px-3 py-2">Preço</th>
                        <th className="text-right text-gray-400 text-xs px-3 py-2">IVA</th>
                        <th className="text-center text-gray-400 text-xs px-3 py-2">Lote</th>
                        <th className="text-center text-gray-400 text-xs px-3 py-2">Validade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selected.items || []).length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center text-gray-500 py-6">Sem artigos</td>
                        </tr>
                      ) : (
                        (selected.items || []).map((item, i) => (
                          <tr key={i} className="border-b border-gray-700/50">
                            <td className="px-3 py-2 text-gray-300">{item.product_name || "—"}</td>
                            <td className="px-3 py-2 text-right text-gray-300">{item.quantity ?? "—"}</td>
                            <td className="px-3 py-2 text-right text-gray-300">{Number(item.unit_price || 0).toFixed(2)}€</td>
                            <td className="px-3 py-2 text-right text-gray-300">{item.vat_rate ?? "—"}%</td>
                            <td className="px-3 py-2 text-center text-blue-400">{item.lot_number || "—"}</td>
                            <td className="px-3 py-2 text-center text-yellow-400">{item.expiry_date || "—"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {selected.notes && (
                <div>
                  <h3 className="text-white font-semibold mb-2">Notas</h3>
                  <div className="bg-gray-800 rounded-xl p-4 text-gray-300 text-sm whitespace-pre-wrap">
                    {selected.notes}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-800">
              <button
                onClick={() => selected && openEditInvoice(selected)}
                className="bg-blue-700 hover:bg-blue-600 text-white py-2.5 rounded-xl text-sm px-5"
              >
                Editar fatura
              </button>
              <button
                onClick={() => selected && deleteInvoice(selected)}
                className="bg-red-800 hover:bg-red-700 text-white py-2.5 rounded-xl text-sm px-5"
                disabled={saving}
              >
                Apagar fatura
              </button>
              <button
                onClick={() => { setShowDetail(false); setSelected(null); }}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-xl text-sm"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Pagamento */}
      {showPayment && selected && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-white">Registar Pagamento</h2>
              <button onClick={()=>setShowPayment(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5"/></button>
            </div>
            <div className="bg-gray-800 rounded-xl p-4 mb-4">
              <p className="text-white font-medium">{selected.supplier_name}</p>
              <p className="text-gray-400 text-sm">Fatura: {selected.invoice_number}</p>
              <div className="flex justify-between mt-2 text-sm">
                <span className="text-gray-400">Total: <span className="text-white font-bold">{selected.total?.toFixed(2)}€</span></span>
                <span className="text-gray-400">Pago: <span className="text-green-400 font-bold">{(selected.amount_paid||0).toFixed(2)}€</span></span>
                <span className="text-gray-400">Dívida: <span className="text-red-400 font-bold">{(selected.total-(selected.amount_paid||0)).toFixed(2)}€</span></span>
              </div>
            </div>
            <div className="space-y-3">
              {[["Valor €","amount","number"],["Data","date","date"],["Referência","reference","text"]].map(([label,field,type])=>(
                <div key={field}>
                  <label className="block text-gray-400 text-xs mb-1">{label}</label>
                  <input type={type} step={type==="number"?"0.01":undefined} value={payment[field]} onChange={e=>setPayment(p=>({...p,[field]:type==="number"?parseFloat(String(e.target.value).replace(',', '.'))||0:e.target.value}))} className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600" />
                </div>
              ))}
              <div>
                <label className="block text-gray-400 text-xs mb-1">Método</label>
                <select value={payment.method} onChange={e=>setPayment(p=>({...p,method:e.target.value}))} className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-green-600">
                  {Object.entries(METHOD_MAP).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={()=>setShowPayment(false)} className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2.5 rounded-xl text-sm">Cancelar</button>
              <button onClick={registerPayment} disabled={saving||payment.amount<=0} className="flex-1 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm flex items-center justify-center gap-2">
                <Check className="w-4 h-4"/>{saving?"A registar...":"Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
